import { useCallback, useState } from 'react'
import Confirm from '../components/Confirm'
import useTranslation from './useTranslation'
import type { Order } from '../types'

/**
 * useSubmitOrder — flux UNIQUE de « Soumettre » (commande retail user-client → webhook submit) /
 * « Synchroniser » (commande classique → locked4sync), partagé par tous les points d'appel
 * (menus OrderDetail / Orders / CustomerDetail). Affiche une popup de confirmation puis exécute :
 *   - commande retail → submitRetailOrder (le serveur décide wait_n2/wait_n1/error + perms)
 *   - commande classique → updateOrder(status: locked4sync, readonly: true)
 *
 * Dépendances injectées (pas d'appel interne à useSales — dont l'effet de dérivation du tarif).
 */
export interface SubmitOrderDeps {
   submitRetailOrder: (order: Order) => Promise<string | null>
   updateOrder: (orderId: string, attribs: Partial<Order>) => Promise<unknown>
   isRetailOrder: (order: Order | null | undefined) => boolean
   lockedForSyncStatus: string
}

export default function useSubmitOrder(deps: SubmitOrderDeps) {
   const { submitRetailOrder, updateOrder, isRetailOrder, lockedForSyncStatus } = deps
   const { trans } = useTranslation()

   const [order, setOrder] = useState<Order | null>(null)
   const [onDone, setOnDone] = useState<(() => void) | null>(null)
   const [show, setShow] = useState(false)

   const requestSubmit = useCallback((ord: Order | null | undefined, done?: () => void) => {
      if (!ord?.id) return
      setOrder(ord)
      setOnDone(() => done ?? null)
      setShow(true)
   }, [])

   const run = useCallback(async () => {
      if (!order) return
      const done = onDone
      const o = order
      setShow(false)
      setOrder(null)
      setOnDone(null)
      if (isRetailOrder(o)) await submitRetailOrder(o)
      else await updateOrder(o.id as string, { status: lockedForSyncStatus as Order['status'], readonly: true })
      done?.()
   }, [order, onDone, isRetailOrder, submitRetailOrder, updateOrder, lockedForSyncStatus])

   const cancel = useCallback(() => {
      setShow(false)
      setOrder(null)
      setOnDone(null)
   }, [])

   // Retail → « Soumettre » (transmis à un valideur) ; classique → « Synchroniser ».
   const isRetail = isRetailOrder(order)
   const submitPopups = (
      <>
         {show && (
            <Confirm
               yesno
               title={trans(isRetail ? '?Soumettre' : '?Synchronisation')}
               text={isRetail ? trans('?confirmer_soumission_retail', 1) : trans('?confirmer_synchronisation_bdc')}
               onOK={run}
               onCancel={cancel}
            />
         )}
      </>
   )

   return { requestSubmit, submitPopups }
}
